import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger,
  DialogFooter 
} from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { 
  FileText, 
  Plus, 
  Printer,
  Clock,
  User,
  Search,
  Download,
  Mail,
  MessageSquare,
  MoreHorizontal,
  Eye,
  Upload,
  FolderOpen
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { DragDropUploader, AttachmentsList } from '../components/FileUpload';
import { toast } from 'sonner';

// ICRCL Details
const ICRCL_NAME = "Innovative Care and Rehab Centre Limited (ICRCL)";
const ICRCL_ADDRESS = "City Gate House, 246-250 Romford Road, London, E7 9HZ";
const ICRCL_PHONE = "+44 7380 350059";
const ICRCL_EMAIL = "info@icrcl.co.uk";

export default function ProgressNotesPage() {
  const { api, user } = useAuth();
  const [notes, setNotes] = useState([]);
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedNote, setSelectedNote] = useState(null);
  
  const [formData, setFormData] = useState({
    patient_id: '',
    session_date: new Date().toISOString().split('T')[0],
    session_type: 'therapy_session',
    duration_minutes: 60,
    // SOAP Format
    subjective: '',
    objective: '',
    assessment: '',
    plan: '',
    // Additional fields
    goals_addressed: '',
    activities_performed: '',
    patient_response: '',
    recommendations: '',
    next_session_date: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [notesRes, patientsRes] = await Promise.all([
        api.get('/progress-notes').catch(() => ({ data: [] })),
        api.get('/patients')
      ]);
      setNotes(notesRes.data);
      setPatients(patientsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/progress-notes', {
        ...formData,
        therapist_id: user.id
      });
      toast.success('Progress note saved successfully');
      setDialogOpen(false);
      fetchData();
      setFormData({
        patient_id: '',
        session_date: new Date().toISOString().split('T')[0],
        session_type: 'therapy_session',
        duration_minutes: 60,
        subjective: '',
        objective: '',
        assessment: '',
        plan: '',
        goals_addressed: '',
        activities_performed: '',
        patient_response: '',
        recommendations: '',
        next_session_date: ''
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save progress note');
    }
  };

  const handlePrint = (note) => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Progress Note - ${note.patient_name}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 40px; }
            .header { text-align: center; border-bottom: 2px solid #1e3a5f; padding-bottom: 20px; margin-bottom: 20px; }
            .logo { font-size: 24px; font-weight: bold; color: #1e3a5f; }
            .patient-info { background: #f8fafc; padding: 15px; margin-bottom: 20px; }
            .section { margin-bottom: 20px; }
            .section-title { font-weight: bold; color: #1e3a5f; border-bottom: 1px solid #e2e8f0; padding-bottom: 5px; margin-bottom: 10px; }
            .soap-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; }
            .footer { margin-top: 40px; border-top: 1px solid #e2e8f0; padding-top: 20px; }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="logo">ICRCL - Innovative Care and Rehab Centre Limited</div>
            <p>City Gate House, 246-250 Romford Road, London, E7 9HZ</p>
            <p>Tel: +44 7380 350059 | Email: info@icrcl.co.uk</p>
          </div>
          <h2 style="text-align: center;">PROGRESS NOTE</h2>
          <div class="patient-info">
            <strong>Patient:</strong> ${note.patient_name}<br>
            <strong>Date:</strong> ${new Date(note.session_date).toLocaleDateString('en-GB')}<br>
            <strong>Session Type:</strong> ${note.session_type?.replace(/_/g, ' ')}<br>
            <strong>Duration:</strong> ${note.duration_minutes} minutes<br>
            <strong>Therapist:</strong> ${note.therapist_name}
          </div>
          <div class="soap-grid">
            <div class="section">
              <div class="section-title">S - Subjective</div>
              <p>${note.subjective || 'N/A'}</p>
            </div>
            <div class="section">
              <div class="section-title">O - Objective</div>
              <p>${note.objective || 'N/A'}</p>
            </div>
            <div class="section">
              <div class="section-title">A - Assessment</div>
              <p>${note.assessment || 'N/A'}</p>
            </div>
            <div class="section">
              <div class="section-title">P - Plan</div>
              <p>${note.plan || 'N/A'}</p>
            </div>
          </div>
          <div class="section">
            <div class="section-title">Goals Addressed</div>
            <p>${note.goals_addressed || 'N/A'}</p>
          </div>
          <div class="section">
            <div class="section-title">Activities Performed</div>
            <p>${note.activities_performed || 'N/A'}</p>
          </div>
          <div class="section">
            <div class="section-title">Patient Response</div>
            <p>${note.patient_response || 'N/A'}</p>
          </div>
          <div class="section">
            <div class="section-title">Recommendations</div>
            <p>${note.recommendations || 'N/A'}</p>
          </div>
          <div class="footer">
            <p><strong>Therapist Signature:</strong> _______________________</p>
            <p><strong>Date:</strong> ${new Date().toLocaleDateString('en-GB')}</p>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const handleDownload = (note) => {
    toast.success(`Downloading progress note for ${note.patient_name}`);
  };

  const handleEmail = (note) => {
    toast.success(`Email progress note - From: ${ICRCL_EMAIL}`);
  };

  const handleSMS = (note) => {
    toast.success(`SMS notification - From: ${ICRCL_PHONE}`);
  };

  const openUploadDialog = (note) => {
    setSelectedNote(note);
    setUploadDialogOpen(true);
  };

  const filteredNotes = notes.filter(note => 
    note.patient_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-[#1e3a5f]">Progress Notes</h1>
          <p className="text-slate-500 mt-1">SOAP format clinical progress documentation</p>
        </div>
        
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#1e3a5f] hover:bg-[#1e3a5f]/90 rounded-full" data-testid="new-progress-note-btn">
              <Plus className="w-4 h-4 mr-2" />
              New Progress Note
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-[#1e3a5f]">Create Progress Note (SOAP Format)</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Patient *</Label>
                  <Select value={formData.patient_id} onValueChange={(v) => setFormData({...formData, patient_id: v})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select patient..." />
                    </SelectTrigger>
                    <SelectContent>
                      {patients.map(p => (
                        <SelectItem key={p.id} value={p.id}>{p.first_name} {p.last_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Session Date *</Label>
                  <Input type="date" value={formData.session_date} onChange={(e) => setFormData({...formData, session_date: e.target.value})} required />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Session Type</Label>
                  <Select value={formData.session_type} onValueChange={(v) => setFormData({...formData, session_type: v})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="therapy_session">Therapy Session</SelectItem>
                      <SelectItem value="assessment">Assessment</SelectItem>
                      <SelectItem value="review">Review</SelectItem>
                      <SelectItem value="discharge">Discharge</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Duration (minutes)</Label>
                  <Input type="number" value={formData.duration_minutes} onChange={(e) => setFormData({...formData, duration_minutes: parseInt(e.target.value)})} />
                </div>
              </div>

              <div className="bg-slate-50 p-4 rounded-lg space-y-4">
                <h4 className="font-bold text-[#1e3a5f]">SOAP Notes</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-blue-700">S - Subjective</Label>
                    <Textarea 
                      placeholder="Patient's reported symptoms, concerns, and history..."
                      value={formData.subjective} 
                      onChange={(e) => setFormData({...formData, subjective: e.target.value})}
                      rows={3}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-green-700">O - Objective</Label>
                    <Textarea 
                      placeholder="Observable findings, measurements, test results..."
                      value={formData.objective} 
                      onChange={(e) => setFormData({...formData, objective: e.target.value})}
                      rows={3}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-amber-700">A - Assessment</Label>
                    <Textarea 
                      placeholder="Clinical interpretation and analysis..."
                      value={formData.assessment} 
                      onChange={(e) => setFormData({...formData, assessment: e.target.value})}
                      rows={3}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-purple-700">P - Plan</Label>
                    <Textarea 
                      placeholder="Treatment plan, interventions, follow-up..."
                      value={formData.plan} 
                      onChange={(e) => setFormData({...formData, plan: e.target.value})}
                      rows={3}
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Goals Addressed</Label>
                <Textarea value={formData.goals_addressed} onChange={(e) => setFormData({...formData, goals_addressed: e.target.value})} rows={2} />
              </div>

              <div className="space-y-2">
                <Label>Activities Performed</Label>
                <Textarea value={formData.activities_performed} onChange={(e) => setFormData({...formData, activities_performed: e.target.value})} rows={2} />
              </div>

              <div className="space-y-2">
                <Label>Patient Response</Label>
                <Textarea value={formData.patient_response} onChange={(e) => setFormData({...formData, patient_response: e.target.value})} rows={2} />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Recommendations</Label>
                  <Textarea value={formData.recommendations} onChange={(e) => setFormData({...formData, recommendations: e.target.value})} rows={2} />
                </div>
                <div className="space-y-2">
                  <Label>Next Session Date</Label>
                  <Input type="date" value={formData.next_session_date} onChange={(e) => setFormData({...formData, next_session_date: e.target.value})} />
                </div>
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                <Button type="submit" className="bg-[#1e3a5f]">Save Progress Note</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search */}
      <Card className="card-icrcl">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input
              placeholder="Search by patient name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>

      {/* Notes Table */}
      <Card className="card-icrcl">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center">
              <div className="w-8 h-8 border-4 border-[#1e3a5f] border-t-transparent rounded-full animate-spin mx-auto"></div>
            </div>
          ) : filteredNotes.length === 0 ? (
            <div className="p-8 text-center text-slate-500">
              <FileText className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p>No progress notes found</p>
              <Button variant="link" className="text-[#2a9d8f]" onClick={() => setDialogOpen(true)}>
                Create your first progress note
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="table-icrcl">
                  <TableHead>Patient</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Therapist</TableHead>
                  <TableHead>Duration</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredNotes.map((note) => (
                  <TableRow key={note.id} className="hover:bg-slate-50">
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-[#2a9d8f]/10 flex items-center justify-center">
                          <User className="w-5 h-5 text-[#2a9d8f]" />
                        </div>
                        <span className="font-medium">{note.patient_name}</span>
                      </div>
                    </TableCell>
                    <TableCell>{new Date(note.session_date).toLocaleDateString('en-GB')}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{note.session_type?.replace(/_/g, ' ')}</Badge>
                    </TableCell>
                    <TableCell>{note.therapist_name}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Clock className="w-4 h-4 text-slate-400" />
                        {note.duration_minutes} min
                      </div>
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" data-testid="progress-note-actions-menu">
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handlePrint(note)}>
                            <Printer className="w-4 h-4 mr-2" />
                            Print
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleDownload(note)}>
                            <Download className="w-4 h-4 mr-2" />
                            Download PDF
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleEmail(note)}>
                            <Mail className="w-4 h-4 mr-2" />
                            Send via Email
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleSMS(note)}>
                            <MessageSquare className="w-4 h-4 mr-2" />
                            Send SMS
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => openUploadDialog(note)}>
                            <Upload className="w-4 h-4 mr-2" />
                            Upload Documents
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Progress Note Documents Upload Dialog */}
      <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-[#1e3a5f]">
              <FolderOpen className="w-5 h-5 inline mr-2" />
              Progress Note Documents - {selectedNote?.patient_name}
            </DialogTitle>
          </DialogHeader>
          
          {selectedNote && (
            <div className="space-y-6">
              {/* Drag & Drop Upload Zone */}
              <DragDropUploader
                entityType="progress_note"
                entityId={selectedNote.id}
                onUploadComplete={() => {}}
                acceptTypes="all"
                title="Upload Session Documents"
                description="Drag & drop clinical photos, handwritten notes, or observation videos"
              />

              {/* Existing Attachments */}
              <AttachmentsList
                entityType="progress_note"
                entityId={selectedNote.id}
                title="Attached Documents"
                showUploadButton={false}
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
